



































































import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import SsoDeepLink from "@/shared/components/ui/SsoDeepLink.vue";
import {
  HsaBankStatementListItem,
  HsaBankTaxDocumentListItem,
  FetchState,
  HsabDocumentType,
  HsabSsoDestination
} from "@/shared/types";
import { StatementsByYearHsab as CurrentPageRoute } from "@/employee/route-names";
import moment from "moment";
import { useHsaBankTaxDocumentsStore } from "@/employee/store/hsaBankTaxDocuments";
import { useHsaBankStatements } from "@/employee/store/hsaBankStatements";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useHsabAccountsStore } from "@/employee/store/hsabAccounts";
import { v4 as uuid } from "uuid";

interface DocumentItem {
  dateGenerated: moment.Moment;
  linkTitle: string;
  downloadAction: () => Promise<any>;
  key: string;
  isLoading: boolean;
}

@Component({
  components: {
    GenericLoadingSpinner,
    SsoDeepLink
  }
})
export default class HsaBankStatements extends Vue {

  hsaBankTaxDocumentsStore = useHsaBankTaxDocumentsStore();
  hsaBankStatements = useHsaBankStatements();
  featureFlagStore = useFeatureFlagStore();
  hsabAccountStore = useHsabAccountsStore();

  @Prop()
  year: number;

  showDocumentViewerModal = false;

  get hsabStatementsDeeplinkLocation () {
    return HsabSsoDestination.Statements;
  }

  get isLoading () {
    return this.featureFlagStore.featureFlagsLoadedState === false ||
      this.hsaBankStatements.isPageStatementsLoaded === false ||
      this.hsaBankTaxDocumentsStore.isPageDocumentsLoaded === false;
  }

  get nextYear () {
    return this.year + 1;
  }

  get previousYear () {
    return this.year - 1;
  }

  get showNextYear () {
    return this.hsaBankTaxDocumentsStore.canViewNextYearDocuments || this.hsaBankStatements.canViewNextYearStatements;
  }

  get showPreviousYear () {
    return this.hsaBankTaxDocumentsStore.canViewPreviousYearDocuments ||
      this.hsaBankStatements.canViewPreviousYearStatements;
  }

  get toPreviousYear () {
    return {
      name: CurrentPageRoute,
      query: {
        year: this.previousYear
      }
    };
  }

  get toNextYear () {
    return {
      name: CurrentPageRoute,
      query: {
        year: this.nextYear
      }
    };
  }

  get documentItems () {
    return this.hsaBankTaxDocumentsStore.taxDocumentsForYear
      .map(this.mapTaxDocumentItem)
      .concat(
        this.hsaBankStatements.monthlyStatementsForYear
          .filter(s => s.hsabDocumentType !== HsabDocumentType.NonHsaAccountSummary)
          .map(this.mapStatementToDocumentItem)
      );
  }

  get showAccountSummariesDeepLink () {
    return this.hsabAccountStore.userHasNotionalAccounts &&
      this.featureFlagStore.featureFlagsAvailable &&
      !this.featureFlagStore.flags.bend20542RemoveSummariesAndTaxFormDeepLinks;
  }

  mapStatementToDocumentItem (item: HsaBankStatementListItem): DocumentItem {
    const dateGenerated = moment(item.dateGenerated);
    const datePeriodStart = moment(item.datePeriodStart);
    const linkTitle = `${datePeriodStart.format("MMMM")} ${datePeriodStart.format("YYYY")} Account Summary`;
    const openStatementMethod = this.hsaBankStatements.openStatementLocation;
    const downloadAction = () => openStatementMethod({
      identifier: item.identifier,
      linkTitle,
      docType: item.hsabDocumentType
    });

    return {
      dateGenerated,
      downloadAction,
      linkTitle,
      key: uuid(),
      isLoading: this.hsaBankStatements.monthlyStatementPdfs[item.identifier].fetchState === FetchState.Loading ||
        this.hsaBankStatements.monthlyStatementLocations[item.identifier].fetchState === FetchState.Loading
    };
  }

  mapTaxDocumentItem (item: HsaBankTaxDocumentListItem): DocumentItem {
    const dateGenerated = moment(item.dateGenerated);
    const linkTitle = `${item.taxYear} ${item.title}`;
    const openTaxDocumentMethod = this.hsaBankTaxDocumentsStore.openTaxDocumentLocation;

    const downloadAction = () => openTaxDocumentMethod({
      identifier: item.identifier,
      linkTitle
    });

    return {
      dateGenerated,
      downloadAction,
      linkTitle,
      key: uuid(),
      isLoading: this.hsaBankTaxDocumentsStore.taxDocumentPdfs[item.identifier].fetchState === FetchState.Loading ||
        this.hsaBankTaxDocumentsStore.taxDocumentLocations[item.identifier].fetchState === FetchState.Loading
    };
  }

  onDownloadClick (item: DocumentItem) {
    item.downloadAction();
  }

  mounted () {
    this.rerouteIfNecessary();
    this.loadPage();
  }

  rerouteIfNecessary () {
    // This page existed on the route /cash-account/statements?year=YYYY, but HSA Bank wants to move away from
    // the word "statements".  So guard against that route showing in the URL.
    if (this.$route.fullPath.search(/.*\/cash-account\/statements.*/) !== -1) {
      this.$router.replace({
        name: CurrentPageRoute,
        query: {
          year: this.year.toFixed(0)
        }
      });
    }
  }

  @Watch("year")
  loadPage () {
    this.hsaBankStatements.loadStatementsForPage(this.year);
    this.hsaBankTaxDocumentsStore.loadTaxDocumentsForPage(this.year);
  }
}

